/* global axios */
import { USE_MOCK } from './config';
import { mockRequest } from './mock/server';

// Same "read the account id out of the URL" convention as the base
// ApiClient (api/ApiClient.js's accountIdFromRoute) — keeps every thin API
// function below (properties.js, units.js, ...) free of an accountId
// parameter, since every one of them is only ever called from inside an
// account-scoped route.
function accountIdFromPath() {
  const isInsideAccountScopedURLs =
    window.location.pathname.includes('/app/accounts');
  if (isInsideAccountScopedURLs) {
    return window.location.pathname.split('/')[3];
  }
  return '';
}

// Mirrors the `knowledgeProxy` helper already used by store/modules/
// {staffCalendar,schedules,operatingHours,...}.js, just pointed at the
// Real Estate BE's own path prefix (docs/real-estate/be/10-api-reference.md
// §0.1: the Vue app never calls FastAPI directly, only through this proxy).
function proxyUrl(accountId, path) {
  return `/api/v1/accounts/${accountId}/proxy/knowledge/real-estate/accounts/${accountId}${path}`;
}

// The one seam between "talk to the mock" and "talk to the real BE" —
// every api/realEstate/*.js function funnels through this, and it's the
// only file that reads USE_MOCK. Both branches resolve to the same shape:
// `{ data, status }` on success, or a thrown axios-shaped error
// `{ response: { status, data: { detail } } }` on failure — components and
// store actions never need to know which branch answered.
//
// `data` — for a JSON body, a plain object; for an upload, a FormData
// instance (pass `isMultipart: true` alongside it).
//
// `responseType` — axios's option, for file downloads (`'blob'`); the
// response body is then a Blob instead of parsed JSON.
export async function request({
  method = 'get',
  path,
  params,
  data,
  isMultipart = false,
  responseType,
} = {}) {
  if (USE_MOCK) {
    return mockRequest({ method, path, params, data });
  }

  const accountId = accountIdFromPath();
  const config = {
    method,
    url: proxyUrl(accountId, path),
    params,
  };
  if (data !== undefined) {
    config.data = data;
  }
  if (isMultipart) {
    config.headers = { 'Content-Type': 'multipart/form-data' };
  }
  if (responseType) {
    config.responseType = responseType;
  }

  // Session headers (access-token/client/uid) are added automatically by
  // helper/APIHelper.js's axios interceptor — never send api_access_token
  // here, the Portal API surface rejects it with 403 (FE handoff §2).
  const response = await axios(config);
  return { data: response.data, status: response.status };
}
